import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Radio,
  Stack,
  CircularProgress,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useAuth } from "../../../context/AuthContext";

// Mismo patrón que EmparejarEquiposOT (TKR: Radio↔Batería), pero para POC:
// el chip (SIM) y los demás accesorios (Accesorio POC, Software POC, etc.) ya
// fueron asignados a la OT durante la Adición (junto con la Radio POC, vía
// AsignarEquiposVariosOT), y acá en "Asignado" se elige UNA Radio POC y se le
// relacionan uno o más de esos accesorios.
//
// Igual que TKR: se dividen en dos emparejamientos independientes porque son dos
// situaciones distintas: equipos nuevos de esta OT (recién asignados) vs. equipos
// que ya estaban activos en el cliente (se les cambia de a qué radio están
// relacionados).
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };

// Un bloque de emparejamiento (radio POC + accesorios: chips, Accesorio POC, etc.)
// con su propia selección y guardado.
function BloqueEmparejamientoChips({ titulo, radios, accesorios, onEmparejado }) {
  const [radioSeleccionado, setRadioSeleccionado] = useState("");
  const [seleccionados, setSeleccionados] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const toggleAccesorio = (key) => {
    setSeleccionados((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };

  const emparejar = async () => {
    if (!radioSeleccionado || !seleccionados.length) return;
    setGuardando(true);
    try {
      for (const key of seleccionados) {
        const item = accesorios.find((a) => a.key === key);
        if (!item) continue;
        // chips y "varios" (Accesorio POC, Software POC, etc.) viven en tablas
        // distintas con IDs autoincrement independientes — de ahí la key compuesta.
        const equipoUrl = item.fuente === "chip" ? `/api/equipos-chip/${item.id}` : `/api/equipos-varios/${item.id}`;
        const historialUrl = item.fuente === "chip" ? `/api/historial-chip/${item.id_historial}` : `/api/historial-varios/${item.id_historial}`;
        await axios.put(equipoUrl, { relacion: radioSeleccionado });
        if (item.id_historial) {
          await axios.put(historialUrl, { relacion: radioSeleccionado });
        }
      }
      setRadioSeleccionado("");
      setSeleccionados([]);
      setOpenSnackbar(true);
      onEmparejado();
    } catch (err) {
      console.error("Error al emparejar accesorios:", err);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          {titulo}
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={!radioSeleccionado || !seleccionados.length || guardando}
          onClick={emparejar}
          sx={{ minHeight: 26, height: 26, fontSize: 11, whiteSpace: "nowrap" }}
        >
          {guardando ? <CircularProgress size={16} color="inherit" /> : "Emparejar Seleccionados"}
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Radio POC
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 34 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Serie</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!radios.length ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        No hay Radios POC en este grupo.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  radios.map((r) => (
                    <TableRow
                      key={r.serie}
                      hover
                      selected={radioSeleccionado === r.serie}
                      onClick={() => setRadioSeleccionado(r.serie)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Radio size="small" checked={radioSeleccionado === r.serie} onChange={() => setRadioSeleccionado(r.serie)} sx={{ p: 0 }} />
                      </TableCell>
                      <TableCell>{r.serie}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ flex: 1.4, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Accesorios (Chips, Accesorio POC, etc.)
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 34 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Identificador</TableCell>
                  <TableCell sx={headCellSx}>Tipo</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Relación actual</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!accesorios.length ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        No hay accesorios en este grupo.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  accesorios.map((a) => {
                    const checked = seleccionados.includes(a.key);
                    return (
                      <TableRow key={a.key} hover selected={checked}>
                        <TableCell>
                          <Checkbox size="small" checked={checked} onChange={() => toggleAccesorio(a.key)} sx={{ p: 0 }} />
                        </TableCell>
                        <TableCell>{a.identificador}</TableCell>
                        <TableCell>{a.tipo}</TableCell>
                        <TableCell>{a.modelo}</TableCell>
                        <TableCell>{a.relacion || "—"}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Accesorios emparejados correctamente.
        </MuiAlert>
      </Snackbar>
    </Paper>
  );
}

const EmparejarChipsPOC = ({ cod_ot, cod_servicio_cliente }) => {
  const { user } = useAuth();
  const [radios, setRadios] = useState([]);
  const [accesorios, setAccesorios] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = () => {
    setLoading(true);
    // Solo equipo Activo de la sucursal del usuario logueado — mismo criterio
    // que DevolucionEquiposOT/SeleccionarMantenimientoOT. El equipo del
    // historial de esta OT no se filtra: ya está asignado a esta OT.
    const ciudad = user?.sucursal || undefined;
    Promise.all([
      // Sin filtro de "tipo": Radios POC y el resto de accesorios (Accesorio POC,
      // Software POC, etc.) vienen todos de equipo_varios, se separan abajo por tipo.
      axios.get(`/api/historial-varios`, { params: { cod_ot } }),
      axios.get(`/api/equipos-varios`, { params: { estado: "Activo", cod_servicio: cod_servicio_cliente, ciudad } }),
      axios.get(`/api/historial-chip`, { params: { cod_ot } }),
      axios.get(`/api/equipos-chip`, { params: { estado: "Activo", cod_servicio: cod_servicio_cliente, ciudad } }),
    ])
      .then(([resVariosHist, resVariosActivos, resChipHist, resChipActivos]) => {
        const variosHist = (resVariosHist.data || []).filter((r) => r.estado === "Asignado");
        // El interceptor de axios ya aplana "data" en response.data (ver axiosConfig.js).
        const variosActivos = resVariosActivos.data || [];

        // Radio POC: el equipo principal de este emparejamiento, no se relaciona a
        // sí mismo — se separa del resto de "varios".
        const radiosNuevos = variosHist
          .filter((r) => r.tipo === "Radio POC")
          .map((r) => ({ serie: r.serie, origen: "Nueva" }));
        const radiosActivos = variosActivos
          .filter((r) => r.tipo === "Radio POC")
          .map((r) => ({ serie: r.serie, origen: "Activa" }));
        const seriesVistas = new Set();
        const radiosCombinados = [...radiosNuevos, ...radiosActivos].filter((r) => {
          if (seriesVistas.has(r.serie)) return false;
          seriesVistas.add(r.serie);
          return true;
        });
        setRadios(radiosCombinados);

        // Accesorios emparejables: Chips + el resto de "varios" (Accesorio POC,
        // Software POC, etc.) — todos se relacionan a la Radio POC seleccionada.
        const variosAccesoriosNuevos = variosHist
          .filter((r) => r.tipo !== "Radio POC")
          .map((r) => ({
            fuente: "varios",
            id: r.equipo_id,
            id_historial: r.id_historial,
            identificador: r.serie,
            tipo: r.tipo,
            modelo: r.modelo,
            relacion: r.relacion_actual,
            origen: "Nueva",
          }));
        const variosAccesoriosActivos = variosActivos
          .filter((r) => r.tipo !== "Radio POC")
          .map((r) => ({
            fuente: "varios",
            id: r.ID,
            id_historial: null,
            identificador: r.serie,
            tipo: r.tipo,
            modelo: r.modelo,
            relacion: r.relacion,
            origen: "Activa",
          }));

        const chipsNuevos = (resChipHist.data || [])
          .filter((c) => c.estado === "Asignado")
          .map((c) => ({
            fuente: "chip",
            id: c.equipo_id,
            id_historial: c.id_historial,
            identificador: c.numero,
            tipo: "Chip",
            modelo: c.modelo,
            relacion: c.relacion_actual,
            origen: "Nueva",
          }));
        const chipsActivos = (resChipActivos.data?.rows || []).map((c) => ({
          fuente: "chip",
          id: c.ID,
          id_historial: null,
          identificador: c.numero,
          tipo: "Chip",
          modelo: c.modelo,
          relacion: c.relacion,
          origen: "Activa",
        }));

        const vistos = new Set();
        const accesoriosCombinados = [...variosAccesoriosNuevos, ...variosAccesoriosActivos, ...chipsNuevos, ...chipsActivos]
          .filter((a) => {
            if (!a.id) return false;
            const clave = `${a.fuente}-${a.id}`;
            if (vistos.has(clave)) return false;
            vistos.add(clave);
            return true;
          })
          .map((a) => ({ ...a, key: `${a.fuente}-${a.id}` }));
        setAccesorios(accesoriosCombinados);
      })
      .catch((err) => console.error("Error al cargar radios POC/accesorios para emparejar", err))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [cod_ot, cod_servicio_cliente]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const radiosOT = radios.filter((r) => r.origen === "Nueva");
  const accesoriosOT = accesorios.filter((a) => a.origen === "Nueva");
  const radiosCliente = radios.filter((r) => r.origen === "Activa");
  const accesoriosCliente = accesorios.filter((a) => a.origen === "Activa");

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
        Emparejar Accesorios
      </Typography>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems="stretch">
        <BloqueEmparejamientoChips
          titulo="Equipos de la OT"
          radios={radiosOT}
          accesorios={accesoriosOT}
          onEmparejado={cargar}
        />
        <BloqueEmparejamientoChips
          titulo="Equipos del Cliente"
          radios={radiosCliente}
          accesorios={accesoriosCliente}
          onEmparejado={cargar}
        />
      </Stack>
    </Box>
  );
};

export default EmparejarChipsPOC;
